import { TextureLoader } from 'three';
import { UpdateMaterial } from '../components/gridpagefunction/materialFunction';
import { applyPropertyOnEntry } from './ThreeJsUtils/materialPropertyEditor';
import {
  applyObjectMapSlotStates,
  restoreMeshMaterialBindings,
} from './ThreeJsUtils/materialMaps';
import store from '../redux/store';
import {
  remove_assets_by_id,
  removeSceneObject,
  removeIntersectedObject,
  selected_object,
  addSceneMesh,
  addSceneImage,
  addSceneVideo,
  addSceneAudio,
  addSceneText,
  setPushIntersectedObject,
} from '../redux/Slice/gridSlice';
import { removeParticle, addParticle } from '../redux/Slice/particleSlice';
import { removeLight, addLight, removeCamera, addCamera } from '../redux/Slice/gridSlice';
import {
  restoreGridHistoryState,
  applyGridHistorySelection,
  resolveHistorySelectionTarget,
  getGridHistoryScene,
} from './gridHistory';
import { applyGridTransformUpdate } from './ThreeJsUtils/syncObjectTransform';
import { suppressGridHistory } from './gridHistorySuppress';

/**
 * Base Command class
 * All commands must implement execute() and undo() methods
 */
class Command {
  execute() {
    throw new Error('Execute method must be implemented');
  }

  undo() {
    throw new Error('Undo method must be implemented');
  }
}

/**
 * AddObjectCommand: Adds an object to the scene
 * Tracks Redux state for proper undo/redo
 */
class AddObjectCommand extends Command {
  constructor(scene, object, objectType = 'mesh', reduxData = null) {
    super();
    this.scene = scene;
    this.object = object;
    this.objectType = objectType; // 'mesh', 'image', 'video', 'audio', 'text', 'light', 'camera', 'particle'
    this.reduxData = reduxData; // Store Redux action data for proper restoration
  }

  execute() {
    if (!this.scene || !this.object) return;
    
    // Only add if not already in scene
    if (!this.scene.children.includes(this.object)) {
      this.scene.add(this.object);
    }
    
    // Update Redux state based on object type
    if (this.reduxData) {
      switch (this.objectType) {
        case 'mesh':
          store.dispatch(addSceneMesh(this.object));
          break;
        case 'image':
          store.dispatch(addSceneImage(this.object));
          break;
        case 'video':
          store.dispatch(addSceneVideo(this.object));
          break;
        case 'audio':
          store.dispatch(addSceneAudio(this.object));
          break;
        case 'text':
          store.dispatch(addSceneText(this.object));
          break;
        case 'light':
          if (this.reduxData.lightData) {
            store.dispatch(addLight(this.reduxData.lightData));
          }
          break;
        case 'camera':
          if (this.reduxData.cameraData) {
            store.dispatch(addCamera(this.reduxData.cameraData));
          }
          break;
        case 'particle':
          if (this.reduxData.particleData) {
            store.dispatch(addParticle(this.reduxData.particleData));
          }
          break;
      }
    }
    
    // Add to intersected objects if it's a selectable object
    if (this.object.visible && !this.object.name?.endsWith('-helper')) {
      store.dispatch(setPushIntersectedObject(this.object));
    }
  }

  undo() {
    if (!this.scene || !this.object) return;
    
    // Remove from scene
    this.scene.remove(this.object);
    
    // Update Redux state
    const objectId = this.object._id || this.object.id || this.object.url || this.object.name;
    
    switch (this.objectType) {
      case 'mesh':
      case 'image':
      case 'video':
      case 'audio':
      case 'text':
        store.dispatch(
          remove_assets_by_id({
            type: this.objectType,
            id: objectId,
            url: objectId,
          })
        );
        break;
      case 'light':
        store.dispatch(removeLight(this.object.name));
        break;
      case 'camera':
        store.dispatch(removeCamera(this.object.name));
        break;
      case 'particle':
        store.dispatch(removeParticle(this.object.name));
        break;
    }
    
    // Remove from intersected objects
    store.dispatch(removeIntersectedObject(this.object));
    store.dispatch(removeSceneObject(this.object));
    
    // Clear selection if this object was selected
    const currentSelected = store.getState().assets_object.select_object;
    if (currentSelected === this.object || currentSelected?.name === this.object.name) {
      store.dispatch(selected_object(null));
    }
  }
}

/**
 * RemoveObjectCommand: Removes an object from the scene
 * Properly tracks Redux state for restoration
 */
class RemoveObjectCommand extends Command {
  constructor(scene, object, objectType = 'mesh', reduxData = null) {
    super();
    this.scene = scene;
    this.object = object;
    this.objectType = objectType;
    this.reduxData = reduxData; // Store Redux data for restoration
    this.parent = object?.parent; // Store parent for proper restoration
    this.index = object?.parent ? object.parent.children.indexOf(object) : -1;
    
    // Store object state BEFORE removal (in constructor, not execute)
    if (object) {
      this.objectState = {
        position: object.position.clone(),
        rotation: object.rotation.clone(),
        scale: object.scale.clone(),
        visible: object.visible,
        userData: JSON.parse(JSON.stringify(object.userData || {})),
      };
    }
  }

  execute() {
    if (!this.scene || !this.object) return;
    
    // Remove from scene
    this.scene.remove(this.object);
    
    
    // Update Redux state
    const objectId =this.object.name || this.object._id || this.object.id || this.object.url || this.object.name;
    
    switch (this.objectType) {
      case 'mesh':
      case 'image':
      case 'video':
      case 'audio':
      case 'text':
        store.dispatch(
          remove_assets_by_id({
            type: this.objectType,
            id: objectId,
            url: objectId,
          })
        );
        break;
      case 'light':
        store.dispatch(removeLight(this.object.name));
        break;
      case 'camera':
        store.dispatch(removeCamera(this.object.name));
        break;
      case 'particle':
        store.dispatch(removeParticle(this.object.name));
        break;
    }
    
    // Remove from intersected objects
    store.dispatch(removeIntersectedObject(this.object));
    store.dispatch(removeSceneObject(this.object));
    
    // Clear selection if this object was selected
    const currentSelected = store.getState().assets_object.select_object;
    if (currentSelected === this.object || currentSelected?.name === this.object.name) {
      store.dispatch(selected_object(null));
    }
  }

  undo() {
    if (!this.scene || !this.object) return;
    
    // Restore object to scene
    if (this.parent) {
      this.parent.add(this.object);
    } else {
      this.scene.add(this.object);
    }
    
    // Restore object state
    if (this.objectState) {
      this.object.position.copy(this.objectState.position);
      this.object.rotation.copy(this.objectState.rotation);
      this.object.scale.copy(this.objectState.scale);
      this.object.visible = this.objectState.visible;
      this.object.userData = this.objectState.userData;
    }
    
    // Restore Redux state
    if (this.reduxData) {
      switch (this.objectType) {
        case 'mesh':
          store.dispatch(addSceneMesh(this.object));
          break;
        case 'image':
          store.dispatch(addSceneImage(this.object));
          break;
        case 'video':
          store.dispatch(addSceneVideo(this.object));
          break;
        case 'audio':
          store.dispatch(addSceneAudio(this.object));
          break;
        case 'text':
          store.dispatch(addSceneText(this.object));
          break;
        case 'light':
          if (this.reduxData.lightData) {
            store.dispatch(addLight(this.reduxData.lightData));
          }
          break;
        case 'camera':
          if (this.reduxData.cameraData) {
            store.dispatch(addCamera(this.reduxData.cameraData));
          }
          break;
        case 'particle':
          if (this.reduxData.particleData) {
            store.dispatch(addParticle(this.reduxData.particleData));
          }
          break;
      }
    }
    
    // Restore to intersected objects
    if (this.object.visible && !this.object.name?.endsWith('-helper')) {
      store.dispatch(setPushIntersectedObject(this.object));
    }
  }
}

/**
 * TransformCommand: Applies transform changes and supports undo
 */
class TransformCommand extends Command {
  constructor(object, prevTransform, newTransform) {
    super();
    this.object = object;

    this.prevTransform = {
      position: prevTransform.position.clone(),
      rotation: prevTransform.rotation.clone(),
      scale: prevTransform.scale.clone(),
    };

    this.newTransform = {
      position: newTransform.position.clone(),
      rotation: newTransform.rotation.clone(),
      scale: newTransform.scale.clone(),
    };
  }

  execute() {
    if (!this.object) return;
    const release = suppressGridHistory();
    try {
      this.object.position.copy(this.newTransform.position);
      this.object.rotation.copy(this.newTransform.rotation);
      this.object.scale.copy(this.newTransform.scale);
      applyGridTransformUpdate(this.object);
    } finally {
      release();
    }
  }

  undo() {
    if (!this.object) return;
    const release = suppressGridHistory();
    try {
      this.object.position.copy(this.prevTransform.position);
      this.object.rotation.copy(this.prevTransform.rotation);
      this.object.scale.copy(this.prevTransform.scale);
      applyGridTransformUpdate(this.object);
    } finally {
      release();
    }
  }
}

/**
 * MaterialCommand: Applies material property changes and supports undo
 */
class MaterialCommand extends Command {
  constructor(object, prevMaterialData, newMaterialData) {
    super();
    this.object = object;
    this.prevMaterialData = JSON.parse(JSON.stringify(prevMaterialData));
    this.newMaterialData = JSON.parse(JSON.stringify(newMaterialData));
  }

  execute() {
    if (!this.object) return;
    const release = suppressGridHistory();
    try {
      this._applyMaterial(this.newMaterialData);
    } finally {
      release();
    }
  }

  undo() {
    if (!this.object) return;
    const release = suppressGridHistory();
    try {
      this._applyMaterial(this.prevMaterialData);
    } finally {
      release();
    }
  }

  _applyMaterial(materialData) {
    // Handle Unlit shader with emission texture to avoid uniforms.emissiveMap error
    if (
      materialData?.shader === 'Unlit' &&
      materialData?.textureImage
    ) {
      const processedData = { ...materialData };

      UpdateMaterial({ object: this.object, addData: processedData });

      if (this.object.type === 'Group' || this.object.type === 'Scene') {
        this.object.traverse((child) => {
          if (
            child.isMesh &&
            child.material &&
            child.material.isMeshBasicMaterial &&
            processedData.textureImage
          ) {
            const textureLoader = new TextureLoader();
            textureLoader.load(processedData.textureImage, (texture) => {
              child.material.map = texture;
              child.material.needsUpdate = true;
            });
          }
        });
      } else if (
        this.object.material &&
        this.object.material.isMeshBasicMaterial &&
        processedData.textureImage
      ) {
        const textureLoader = new TextureLoader();
        textureLoader.load(processedData.textureImage, (texture) => {
          this.object.material.map = texture;
          this.object.material.needsUpdate = true;
        });
      }
    } else {
      UpdateMaterial({ object: this.object, addData: materialData });
    }
  }
}

/**
 * Applies a single Three.js material property change (not persisted to API material_settings).
 */
class ThreeMaterialPropertyCommand extends Command {
  constructor(entry, propertyKey, prevPayload, nextPayload) {
    super();
    this.entry = entry;
    this.propertyKey = propertyKey;
    this.prevPayload = prevPayload;
    this.nextPayload = nextPayload;
  }

  execute() {
    const release = suppressGridHistory();
    try {
      applyPropertyOnEntry(this.entry, this.propertyKey, this.nextPayload);
    } finally {
      release();
    }
  }

  undo() {
    const release = suppressGridHistory();
    try {
      applyPropertyOnEntry(this.entry, this.propertyKey, this.prevPayload);
    } finally {
      release();
    }
  }
}

/**
 * Texture map upload / replace on an object — one undo step per map change.
 * Stores before/after slot states (URL + visibility) for every material touched.
 */
class MaterialTextureMapCommand extends Command {
  constructor(object, mapKey, prevEntries, nextEntries) {
    super();
    this.object = object;
    this.mapKey = mapKey;
    this.prevEntries = prevEntries || [];
    this.nextEntries = nextEntries || [];
  }

  async execute() {
    if (!this.object || !this.mapKey) return;
    const release = suppressGridHistory();
    try {
      await applyObjectMapSlotStates(this.nextEntries, this.mapKey);
    } finally {
      release();
    }
  }

  async undo() {
    if (!this.object || !this.mapKey) return;
    const release = suppressGridHistory();
    try {
      await applyObjectMapSlotStates(this.prevEntries, this.mapKey);
    } finally {
      release();
    }
  }
}

/**
 * GLB material list ops (replace / remove / add / rename) — step-by-step undo
 * of mesh material bindings + unused library + material names.
 */
class MeshMaterialBindingCommand extends Command {
  constructor(assetRoot, beforeSnapshot, afterSnapshot, onRestore = null) {
    super();
    this.assetRoot = assetRoot;
    this.beforeSnapshot = beforeSnapshot;
    this.afterSnapshot = afterSnapshot;
    this.onRestore = onRestore;
  }

  execute() {
    if (!this.assetRoot || !this.afterSnapshot) return;
    const release = suppressGridHistory();
    try {
      restoreMeshMaterialBindings(this.assetRoot, this.afterSnapshot);
      this.onRestore?.(this.afterSnapshot);
    } finally {
      release();
    }
  }

  undo() {
    if (!this.assetRoot || !this.beforeSnapshot) return;
    const release = suppressGridHistory();
    try {
      restoreMeshMaterialBindings(this.assetRoot, this.beforeSnapshot);
      this.onRestore?.(this.beforeSnapshot);
    } finally {
      release();
    }
  }
}

/**
 * DuplicateCommand: Duplicates an object
 */
class DuplicateCommand extends Command {
  constructor(scene, originalObject, duplicateObject, objectType = 'mesh', reduxData = null) {
    super();
    this.scene = scene;
    this.originalObject = originalObject;
    this.duplicateObject = duplicateObject;
    this.objectType = objectType;
    this.reduxData = reduxData;
  }

  execute() {
    if (!this.scene || !this.duplicateObject) return;
    
    this.scene.add(this.duplicateObject);
    
    // Update Redux state
    if (this.reduxData) {
      switch (this.objectType) {
        case 'mesh':
          store.dispatch(addSceneMesh(this.duplicateObject));
          break;
        case 'image':
          store.dispatch(addSceneImage(this.duplicateObject));
          break;
        case 'video':
          store.dispatch(addSceneVideo(this.duplicateObject));
          break;
        case 'audio':
          store.dispatch(addSceneAudio(this.duplicateObject));
          break;
        case 'text':
          store.dispatch(addSceneText(this.duplicateObject));
          break;
        case 'light':
          if (this.reduxData.lightData) {
            store.dispatch(addLight(this.reduxData.lightData));
          }
          break;
        case 'camera':
          if (this.reduxData.cameraData) {
            store.dispatch(addCamera(this.reduxData.cameraData));
          }
          break;
        case 'particle':
          if (this.reduxData.particleData) {
            store.dispatch(addParticle(this.reduxData.particleData));
          }
          break;
      }
    }
    
    // Add to intersected objects
    if (this.duplicateObject.visible && !this.duplicateObject.name?.endsWith('-helper')) {
      store.dispatch(setPushIntersectedObject(this.duplicateObject));
    }
  }

  undo() {
    if (!this.scene || !this.duplicateObject) return;
    
    // Remove duplicate from scene
    this.scene.remove(this.duplicateObject);
    
    // Update Redux state
    const objectId = this.duplicateObject._id || this.duplicateObject.id || this.duplicateObject.url || this.duplicateObject.name;
    
    switch (this.objectType) {
      case 'mesh':
      case 'image':
      case 'video':
      case 'audio':
      case 'text':
        store.dispatch(
          remove_assets_by_id({
            type: this.objectType,
            id: objectId,
            url: objectId,
          })
        );
        break;
      case 'light':
        store.dispatch(removeLight(this.duplicateObject.name));
        break;
      case 'camera':
        store.dispatch(removeCamera(this.duplicateObject.name));
        break;
      case 'particle':
        store.dispatch(removeParticle(this.duplicateObject.name));
        break;
    }
    
    // Remove from intersected objects
    store.dispatch(removeIntersectedObject(this.duplicateObject));
    store.dispatch(removeSceneObject(this.duplicateObject));
    
    // Clear selection if duplicate was selected
    const currentSelected = store.getState().assets_object.select_object;
    if (currentSelected === this.duplicateObject || currentSelected?.name === this.duplicateObject.name) {
      store.dispatch(selected_object(null));
    }
  }
}

/**
 * GridHistoryCommand: full scene + Redux snapshot undo/redo
 */
class GridHistoryCommand extends Command {
  constructor(beforeSnapshot, afterSnapshot, label = 'edit') {
    super();
    this.beforeSnapshot = beforeSnapshot;
    this.afterSnapshot = afterSnapshot;
    this.label = label;
  }

  async execute() {
    await restoreGridHistoryState(this.afterSnapshot);
    const selected = resolveHistorySelectionTarget(
      this.beforeSnapshot,
      this.afterSnapshot,
      getGridHistoryScene(),
      'redo'
    );
    applyGridHistorySelection(selected);
  }

  async undo() {
    await restoreGridHistoryState(this.beforeSnapshot);
    const selected = resolveHistorySelectionTarget(
      this.beforeSnapshot,
      this.afterSnapshot,
      getGridHistoryScene(),
      'undo'
    );
    applyGridHistorySelection(selected);
  }
}

export {
  Command,
  AddObjectCommand,
  RemoveObjectCommand,
  TransformCommand,
  MaterialCommand,
  ThreeMaterialPropertyCommand,
  MaterialTextureMapCommand,
  MeshMaterialBindingCommand,
  DuplicateCommand,
  GridHistoryCommand,
};